import type { CellarGuestsResponse, InviteGuestResponse } from "@corklog/shared";
import { useCallback, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import {
  ApiError,
  fetchGuests,
  inviteGuest,
  revokeGuest,
  setGuestVisibility,
  withdrawInvitation,
} from "../api.js";
import { CellarHeading } from "../components/CellarHeading.js";
import {
  actionButton,
  control,
  fieldLabel,
  resting,
  Select,
} from "../components/controls.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";

/**
 * Who else may read this cellar.
 *
 * **The page is a list of people and not a list of permissions**, because that
 * is the question being asked: who can see this. So the owner stands at the
 * top of the same list as the guests rather than being assumed by the reader —
 * a page that showed only the guests would leave somebody wondering whether it
 * had left them out — and each row carries the address the person was reached
 * at, which is the only thing the owner had to hand when they invited them.
 *
 * **A pending invitation is a row of its own and says what it is waiting
 * for.** Nobody registers themselves here, so an address with no account
 * cannot act on its own invitation: the account is made by hand, and the
 * access appears the day it is. That is an unusual thing for an application to
 * admit, so the sentence under the list admits it in plain Swedish rather than
 * leaving an address sitting there looking broken.
 *
 * **Sharing is the owner's, and a guest is told so in a sentence.** Since
 * `9.2` the row of sections does not show this page to a guest at all — but a
 * link can still be typed or kept in a bookmark, so a guest who arrives is
 * answered here, by name, rather than by a load that appears to have failed.
 *
 * **What they see of the cellar is set here too, from `9.2`.** It is not filed
 * with the cellar's furniture because it is not a fact about the cellar: it is
 * a fact about the people in the list above it, and this page is already the
 * answer to who they are.
 *
 * **From 2026-09-22 it is set on the row rather than under the list**, which
 * is the same argument taken one step further than `9.2` took it. A setting
 * standing below the list said *guests*, and there is no such person — there
 * is Erik, who helps carry the cases home, and there is somebody shown round
 * after dinner. So each switch stands on the row of the person it is about,
 * which is also the only place it can be read as an answer: this page exists
 * to be checked, and a list of names above a switch about all of them is a
 * page that cannot be.
 */
type State =
  | { status: "loading" }
  | { status: "ready"; access: CellarGuestsResponse }
  | { status: "notOwner" }
  | { status: "failed" };

/** Dates in the interface's language, like every other date in it. */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString(locale());
}

/**
 * The day the cellar began recording who was last in it. A row with no stamp
 * is *not since then* rather than *never*: everybody who already had access
 * that morning starts without one, including people who used it the evening
 * before. See `project-scope.md`, 2026-09-27.
 */
const VISITS_RECORDED_FROM = "2026-09-27";

/**
 * What the row says about when this person was last in the cellar.
 *
 * With no stamp, the sentence dates itself from whichever came later — the
 * start of recording or the start of their access — so somebody invited last
 * week reads *not since they were invited*, which is the true one.
 */
function lastVisit(guest: CellarGuestsResponse["guests"][number]): {
  key: "guests.lastVisited" | "guests.notVisitedSince";
  date: string;
} {
  if (guest.lastVisitedAt != null) {
    return { key: "guests.lastVisited", date: day(guest.lastVisitedAt) };
  }
  const granted = day(guest.grantedAt);
  return {
    key: "guests.notVisitedSince",
    date: granted > VISITS_RECORDED_FROM ? granted : VISITS_RECORDED_FROM,
  };
}

export function Guests() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const [email, setEmail] = useState("");
  /** On what footing the next invitation grants access. Guest first: of the
   *  two it is the one that grants less, and a default that granted more
   *  would be a default nobody chose. */
  const [role, setRole] = useState<"Guest" | "Contributor">("Guest");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<InviteGuestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Whose switch failed to save, so the sentence names them. */
  const [visibilityFailed, setVisibilityFailed] = useState<string | null>(null);
  /** Who was just removed, so the way back can be offered by name. */
  const [removed, setRemoved] = useState<CellarGuestsResponse["guests"][number] | null>(
    null,
  );

  const load = useCallback(() => {
    void fetchGuests(cellarId)
      .then((access) => setState({ status: "ready", access }))
      .catch((cause: unknown) => {
        setState(
          cause instanceof ApiError && cause.status === 403
            ? { status: "notOwner" }
            : { status: "failed" },
        );
      });
  }, [cellarId]);

  useReload(load);

  /**
   * Shown at once and put back if the write fails, which is the rule
   * `Producers` already follows for judging a house: a tick that waited for a
   * round trip before moving reads as a tick that did not register, and the
   * next thing somebody does is tick it again.
   *
   * Both switches are sent, because what this changes is *what Erik sees* and
   * that is two booleans — the screen knows both, so sending the pair costs
   * nothing and the request is the complete new state of what it addresses.
   */
  const changeVisibility = (
    guest: CellarGuestsResponse["guests"][number],
    next: { seesPrices: boolean; seesNewWines: boolean },
  ) => {
    setState((current) =>
      current.status === "ready"
        ? {
            status: "ready",
            access: {
              ...current.access,
              guests: current.access.guests.map((row) =>
                row.userId === guest.userId ? { ...row, ...next } : row,
              ),
            },
          }
        : current,
    );
    setVisibilityFailed(null);
    void setGuestVisibility(cellarId, guest.userId, next).catch(() => {
      setVisibilityFailed(guest.name);
      // Back to whatever the cellar actually says, rather than to the opposite
      // of what was asked for — the write may have landed and the answer been
      // lost on the way home.
      load();
    });
  };

  /**
   * Taking somebody's access back, and the way to undo it in the same breath.
   *
   * No confirmation before, which is the rule this application keeps for
   * everything except deleting a lot: nothing is lost here — the notes a guest
   * wrote stay where they are, attributed — and the way back is one tap in the
   * sentence that says what happened. Re-inviting clears the revocation, so
   * the undo is the invite route with the address the row already carries.
   */
  const remove = (guest: CellarGuestsResponse["guests"][number]) => {
    setOutcome(null);
    setError(null);
    void revokeGuest(cellarId, guest.userId)
      .then(() => {
        setRemoved(guest);
        load();
      })
      .catch(() => setError(t("guests.revokeFailed")));
  };

  const withdraw = (invitation: CellarGuestsResponse["pending"][number]) => {
    setOutcome(null);
    setError(null);
    void withdrawInvitation(cellarId, invitation.id)
      .then(() => {
        // Nothing to undo in a sentence: an invitation nobody claimed granted
        // nothing, and writing it again is the same address in the form below.
        setRemoved(null);
        load();
      })
      .catch(() => setError(t("guests.withdrawFailed")));
  };

  const invite = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim() === "" || busy) return;
    setBusy(true);
    setError(null);
    setOutcome(null);
    void inviteGuest(cellarId, email, role)
      .then((result) => {
        // The address is cleared only on the way out, so a rejected one stays
        // in the field to be corrected rather than having to be retyped.
        setEmail("");
        setOutcome(result);
        load();
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof ApiError && cause.status === 409
            ? t("guests.alreadyHasAccess")
            : cause instanceof ApiError && cause.status === 400
              ? t("guests.notAnAddress")
              : t("guests.inviteFailed"),
        );
      })
      .finally(() => setBusy(false));
  };

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="guests" />

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "notOwner" && (
        <p className="text-muted mt-6 max-w-prose text-sm">{t("guests.ownersOnly")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("guests.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <>
          <p className="text-muted mt-2 max-w-prose text-sm">{t("guests.intro")}</p>

          <ul className="border-line mt-8 border-t">
            <li className="border-line flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b py-4">
              <div>
                <span className="font-display text-lg">{state.access.owner.name}</span>
                <span className="text-muted ml-3 text-sm">
                  {state.access.owner.email}
                </span>
              </div>
              <span className="text-muted text-sm">{t("role.Owner")}</span>
            </li>

            {state.access.guests.map((guest) => {
              // The row says what this person sees, and nothing resolves it.
              // Until 2026-09-22 it fell back to a cellar-wide value for the
              // one release an older API could still be serving; that release
              // ended with revision 45 and the fallback left with the column.
              const sees = {
                seesPrices: guest.seesPrices,
                seesNewWines: guest.seesNewWines,
              };
              const visit = lastVisit(guest);
              return (
                <li
                  key={guest.userId}
                  className="border-line flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b py-4"
                >
                  <div>
                    <span className="font-display text-lg">{guest.name}</span>
                    <span className="text-muted ml-3 text-sm">{guest.email}</span>
                    {/* On what footing they reach the cellar. With two kinds of
                        access, a list that said only who would be a list the
                        owner cannot check — and checking it is what this page is
                        for.

                        Drawn unconditionally since 2026-09-22. It was behind a
                        null check for the one release an older API could still
                        be answering, and the row always has a role: the query
                        behind this list excludes the owner, so what is left is
                        one of the two invitable ones. */}
                    <span className="text-muted ml-3 text-sm">
                      {t(`role.${guest.role}`)}
                    </span>

                    {/* What this person is shown of a cellar that is not
                        theirs, on their own row from 2026-09-22.

                        On a line of its own under the name rather than in the
                        right-hand column: that column is how the access ends,
                        and these are what it *grants*. On a phone the two would otherwise
                        wrap into each other, and a checkbox that lands beside
                        *Ta bort* is a checkbox somebody eventually taps by
                        mistake.

                        Ticking is the decision and there is no save button,
                        which is the rule this page already kept when the
                        setting was the cellar's: the whole of it is one
                        boolean, so a second tap to confirm would only be a
                        second tap. */}
                    <span className="text-muted mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
                      <label className="flex items-baseline gap-2">
                        <input
                          type="checkbox"
                          checked={sees.seesPrices}
                          onChange={(event) =>
                            changeVisibility(guest, {
                              ...sees,
                              seesPrices: event.target.checked,
                            })
                          }
                        />
                        <span>{t("guests.seesPrices")}</span>
                      </label>
                      <label className="flex items-baseline gap-2">
                        <input
                          type="checkbox"
                          checked={sees.seesNewWines}
                          onChange={(event) =>
                            changeVisibility(guest, {
                              ...sees,
                              seesNewWines: event.target.checked,
                            })
                          }
                        />
                        <span>{t("guests.seesNewWines")}</span>
                      </label>
                    </span>

                    {/* When their access began and when they were last in the
                        cellar, together: the pair is what says whether access
                        handed out long ago is still used, which is the
                        question to ask before *Ta bort*.

                        Under the switches rather than in the right-hand
                        column, from 2026-09-27. With two dates there, the
                        column grew wide enough that a row with a longer name
                        pushed it onto a line of its own, and the list stopped
                        reading as one table — some rows ending in *Ta bort*,
                        others in a sentence under them. Left alone, the column
                        holds only the way to end the access, and every row
                        lines up. */}
                    <span className="text-muted mt-2 flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm">
                      <span>{t("guests.since", { date: day(guest.grantedAt) })}</span>
                      <span>{t(visit.key, { date: visit.date })}</span>
                      {/* In the application at all — sent to the
                          administrator only, so for anybody else this is
                          never drawn. */}
                      {guest.lastSeenAt != null && (
                        <span>
                          {t("guests.lastSeenAnywhere", { date: day(guest.lastSeenAt) })}
                        </span>
                      )}
                    </span>
                  </div>
                  <span className="text-muted flex items-baseline gap-4 text-sm">
                    <button
                      type="button"
                      onClick={() => remove(guest)}
                      className="hover:text-past underline-offset-4 hover:underline"
                    >
                      {t("guests.revoke")}
                    </button>
                  </span>
                </li>
              );
            })}

            {/* An address with nobody behind it. In the same list rather than
                under a heading of its own, because it is an answer to the same
                question — who reaches this cellar — and the difference is what
                the row says on its right-hand side. */}
            {state.access.pending.map((invitation) => (
              <li
                key={invitation.id}
                className="border-line flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b py-4"
              >
                <div>
                  <span className="font-display text-lg">{invitation.email}</span>
                </div>
                <span className="text-muted flex items-baseline gap-4 text-sm">
                  <span>{t("guests.waiting", { date: day(invitation.invitedAt) })}</span>
                  <button
                    type="button"
                    onClick={() => withdraw(invitation)}
                    className="hover:text-past underline-offset-4 hover:underline"
                  >
                    {t("guests.withdraw")}
                  </button>
                </span>
              </li>
            ))}
          </ul>

          {/* What was just taken back, and the way to put it back. The pattern
              the bottle actions set: act, then offer the way back in the
              notice, because the moment a two-tap mistake is noticed is this
              one. Re-inviting clears the revocation, so this is the ordinary
              invite with an address the page already knows.

              Directly under the list rather than beside the invite form's own
              answer at the foot of the page: the row it is about was at the
              top, and on a phone a confirmation that far from the tap that
              caused it is a confirmation nobody sees. */}
          {removed !== null && (
            <p role="status" className="text-ink mt-4 flex items-baseline gap-4 text-sm">
              <span>{t("guests.revoked", { name: removed.name })}</span>
              <button
                type="button"
                onClick={() => {
                  const back = removed;
                  setRemoved(null);
                  // Back on the footing they were on. Re-inviting is the undo
                  // for a revocation, and an undo that quietly demoted
                  // somebody to a guest would not be one — which is what the
                  // `?? "Guest"` here would have done on the day an older API
                  // sent no role. The row carries one now, so the guess is
                  // gone rather than left to fire in the one case it is wrong.
                  void inviteGuest(cellarId, back.email, back.role)
                    .then(() => {
                      setOutcome(null);
                      load();
                    })
                    .catch(() => setError(t("guests.inviteFailed")));
                }}
                className="text-accent underline-offset-4 hover:underline"
              >
                {t("guests.undoRevoke")}
              </button>
            </p>
          )}

          {state.access.guests.length === 0 && state.access.pending.length === 0 && (
            <p className="text-muted mt-4 max-w-prose text-sm">{t("guests.onlyYou")}</p>
          )}

          {state.access.pending.length > 0 && (
            <p className="text-muted mt-4 max-w-prose text-sm">
              {t("guests.pendingExplained")}
            </p>
          )}

          {/* What the two switches on each row mean, said once under the list
              rather than beside every name.

              They are the one thing on this page whose effect is invisible
              from it — the owner sees prices whatever they tick, so nothing
              here shows them what a guest's screen now looks like. Two
              sentences is what that costs, and they say what the unticked
              state *is* rather than what the box does, because the default is
              the surprising half: both are off, and neither was asked for by
              the person they are about. */}
          {state.access.guests.length > 0 && (
            <p className="text-muted mt-6 max-w-prose text-sm">
              {t("guests.visibilityExplained")}
            </p>
          )}

          {visibilityFailed !== null && (
            <p role="alert" className="text-past mt-2 text-sm">
              {t("guests.visibilityFailed", { name: visibilityFailed })}
            </p>
          )}

          <form
            className="border-line mt-8 flex flex-wrap items-end gap-4 border-t pt-6"
            onSubmit={invite}
          >
            <div className="w-72">
              <label className={fieldLabel} htmlFor="guest-email">
                {t("guests.email")}
              </label>
              <input
                id="guest-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={`${control} ${resting} mt-1`}
              />
            </div>
            {/* On what footing. Two options and never `Owner`: making somebody
                the owner is a transfer, which is offered and accepted and
                takes the cellar away from whoever offers it — the same act
                without the half that makes it safe. It is reached from this
                page, by its own link, and not from this dropdown.

                A select rather than two buttons, because this is one decision
                with two answers and not two different acts; and the guest
                stands first because it grants less. */}
            <div className="w-56">
              <label className={fieldLabel} htmlFor="guest-role">
                {t("guests.role")}
              </label>
              <div className="mt-1">
                <Select
                  id="guest-role"
                  value={role}
                  onChange={(next) => setRole(next === "Contributor" ? next : "Guest")}
                >
                  <option value="Guest">{t("role.Guest")}</option>
                  <option value="Contributor">{t("role.Contributor")}</option>
                </Select>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy || email.trim() === ""}
              className={actionButton}
            >
              {t("guests.invite")}
            </button>
            {/* What the chosen footing means, on a line of its own under the
                row rather than inside the column it belongs to.

                Inside it, it was the thing that broke the row: the fields are
                aligned along their bottom edge — which is what puts an input,
                a select and a button on one line whatever their labels say —
                and a paragraph under the select made that column two lines
                taller, so the address field sank while the select stayed up.
                A sentence that changes as the dropdown changes is also wider
                than the dropdown, and it reads better across the measure than
                squeezed into 14rem. */}
            <p className="text-muted mt-1 max-w-prose basis-full text-sm">
              {t(role === "Contributor" ? "guests.roleTaster" : "guests.roleGuest")}
            </p>
          </form>

          {error !== null && (
            <p role="alert" className="text-past mt-4 text-sm">
              {error}
            </p>
          )}

          {/* What came of it, in the words of what actually happened: an
              account that existed can read the cellar now, and one that does
              not is waiting for itself. Two sentences rather than one, because
              the difference is the whole of what the person needs to know. */}
          {outcome !== null && (
            <p className="text-ink mt-4 max-w-prose text-sm">
              {outcome.outcome === "granted"
                ? t("guests.granted", { name: outcome.name ?? outcome.email })
                : t("guests.pending", { email: outcome.email })}
            </p>
          )}

          {/* The way out of owning the cellar, at the foot of the page that is
              about who reaches it — which is the same question at its limit.
              A link and not a form: what it leads to has to say the whole of
              what it does before anything is typed, and that is four sentences
              rather than a field. Last on the page and quiet, because it is
              done once in the life of a cellar, if ever. */}
          <p className="border-line text-muted mt-8 max-w-prose border-t pt-6 text-sm">
            <Link
              to={`/kallare/${cellarId}/overlata`}
              className="text-accent underline-offset-4 hover:underline"
            >
              {t("transfer.title")}
            </Link>
            <span className="ml-3">{t("transfer.fromGuests")}</span>
          </p>
        </>
      )}
    </section>
  );
}
